<script setup lang="ts" generic="T">
import PositionablePopover from '@/core/dialogs/PositionablePopover.vue';
import EmptyPlaceholder from '@/core/elements/EmptyPlaceholder.vue';
import SearchInput from '@/core/elements/SearchInput.vue';
import { EMenuAction, getActionFromKey, maintainScrollVisibility } from '@/utils/menu.utils';
import { useDebounce } from '@vueuse/core';
import { EIcon } from 'common/enum/misc.enum';
import { computed, nextTick, onMounted, ref, toRefs, useTemplateRef, watch } from 'vue';

const slots = defineSlots<{
  default(props: { option: T; active: boolean }): any;
  all?(props: { active: boolean }): any;
  emptyPlaceholder?: (props: { query: string; results: T[] }) => any;
  moreResults?: (props: { count: number }) => any;
}>();

const props = withDefaults(
  defineProps<{
    options: T[];
    canSelectAll?: boolean;
    disabled?: boolean;
    emptyMessage?: string;
    iconName?: EIcon;
    filterCallback?: (data: T[], query: string) => T[];
    label?: string;
    placeholder?: string;
    searchQuery?: string;
    size?: 'sm' | 'md';
    showResultsOnlyOnQuery?: boolean;
    autocomplete?: string;
    maxResults?: number;
    autofocus?: boolean;
  }>(),
  {
    filterCallback: undefined,
    maxResults: undefined,
    label: undefined,
    searchQuery: undefined,
    emptyMessage: undefined,
    size: 'sm',
    iconName: EIcon.PLUS,
    showResultsOnlyOnQuery: false,
    autocomplete: 'on',
    placeholder: undefined,
  },
);
const { options, filterCallback, disabled, canSelectAll, searchQuery, maxResults } = toRefs(props);

const emit = defineEmits<{
  'option-selected': [value: T | null | 'all'];
  'update:query': [value: string];
  'enter-pressed': [query: string, results: T[]];
}>();

const searchQueryInternal = ref('');
const debouncedSearchQueryInternal = useDebounce(searchQueryInternal, 300);

const menuOpen = ref(false);
const activeOption = ref<T | null | 'all'>(null);

const inputRef = useTemplateRef('inputRef');
const menuDialogRef = useTemplateRef('menuDialogRef');
const menuRef = useTemplateRef('menuRef');
const activeOptionRef = ref<HTMLElement | null>(null);

const selectOption = (option: T | 'all'): void => {
  activeOption.value = option;
  updateMenuState(false);
  searchQueryInternal.value = '';
  emit('option-selected', option);
};

const onOptionChange = (val: T | null): void => {
  activeOption.value = val;
  updateMenuScroll();
};

const getUpdatedOptionValue = (current: T | null, action: EMenuAction): T | null => {
  const currentIndex = options.value.findIndex((o) => o === current);
  switch (action) {
    case EMenuAction.First:
      return options.value[0];
    case EMenuAction.Last:
      return options.value[options.value.length - 1];
    case EMenuAction.Previous:
      return options.value[currentIndex - 1] ? options.value[currentIndex - 1] : options.value[currentIndex];
    case EMenuAction.Next:
      return options.value[currentIndex + 1] ? options.value[currentIndex + 1] : options.value[currentIndex];
    default:
      return current;
  }
};

const updateMenuState = (state: boolean): void => {
  menuOpen.value = state;
  if (state) {
    activeOption.value = null;
    menuDialogRef.value?.show();
  } else {
    menuDialogRef.value?.hide();
  }
  updateMenuScroll();
};

const handleMenuKeydown = (event: KeyboardEvent): void => {
  const action = getActionFromKey(event, menuOpen.value);
  switch (action) {
    case EMenuAction.Next:
    case EMenuAction.Last:
    case EMenuAction.First:
    case EMenuAction.Previous:
      event.preventDefault();
      return onOptionChange(getUpdatedOptionValue(activeOption.value, action));
    case EMenuAction.CloseSelect:
    case EMenuAction.Space:
      event.preventDefault();
      return selectOption(activeOption.value);
    case EMenuAction.Close:
      event.preventDefault();
      return updateMenuState(false);
    case EMenuAction.Open:
      event.preventDefault();
      return updateMenuState(true);
  }
};

const updateMenuScroll = (): void => {
  if (!activeOptionRef.value || !menuRef.value || !menuOpen.value) return;
  maintainScrollVisibility(activeOptionRef.value, menuRef.value);
  void nextTick(() => {
    activeOptionRef.value?.scrollIntoView(false);
  });
};

const refActiveOption = (el: unknown, val: T | 'all'): void => {
  if (val === activeOption.value && el) {
    activeOptionRef.value = el as HTMLElement;
  }
};

const filteredOptions = computed<T[]>(() => {
  const query = debouncedSearchQueryInternal.value.trim().toLowerCase();
  return filterCallback.value ? filterCallback.value(options.value, query) : options.value;
});
// Only the first maxResults matches are rendered; the rest is announced so the user narrows the query.
const visibleOptions = computed<T[]>(() =>
  maxResults.value === undefined ? filteredOptions.value : filteredOptions.value.slice(0, maxResults.value),
);
const hiddenResultCount = computed<number>(() => filteredOptions.value.length - visibleOptions.value.length);

const showEmptyPlaceholder = computed<boolean>(() => {
  if (canSelectAll.value) return !!searchQueryInternal.value && !!slots.all;
  return !filteredOptions.value.length;
});

const selectMatchingOption = (): void => {
  if (!searchQueryInternal.value.trim()) return;

  if (filteredOptions.value.length === 1) {
    selectOption(filteredOptions.value[0]);
    return;
  }

  emit('enter-pressed', searchQueryInternal.value, filteredOptions.value);
};

const reset = (): void => {
  activeOption.value = null;
  searchQueryInternal.value = '';
  updateMenuState(false);
  emit('update:query', '');
};

watch(debouncedSearchQueryInternal, () => {
  if (debouncedSearchQueryInternal.value.trim()) {
    updateMenuState(true);
  }
  emit('update:query', debouncedSearchQueryInternal.value);
});

watch(
  searchQuery,
  () => {
    searchQueryInternal.value = searchQuery.value ?? '';
  },
  { immediate: true },
);

// an autofocused select shows its options right away instead of waiting for a click
onMounted(() => {
  if (props.autofocus) updateMenuState(true);
});

defineExpose({
  reset,
});
</script>

<template>
  <div>
    <SearchInput
      ref="inputRef"
      v-model="searchQueryInternal"
      :label="label"
      :placeholder="placeholder"
      :icon-name="iconName"
      :class="{
        'outline-2 outline-tint outline-offset-[-2px]': menuOpen,
        'input-text-labeled': size === 'md',
      }"
      :disabled="disabled"
      :autocomplete="autocomplete"
      :autofocus="autofocus"
      @click="updateMenuState(true)"
      @keydown.enter.prevent="selectMatchingOption"
    />
    <PositionablePopover
      v-if="!showResultsOnlyOnQuery || (showResultsOnlyOnQuery && searchQueryInternal?.length > 0)"
      ref="menuDialogRef"
      scroll-mode="content"
      :anchor-element="inputRef?.$el"
      full-width
      @hide="menuOpen = false"
    >
      <div class="flex min-h-0 flex-col rounded-md shadow-popup">
        <div class="flex min-h-0 flex-col overflow-hidden rounded-md">
          <ul
            ref="menuRef"
            :class="[
              'bg-white focus:outline-none rounded-md',
              'w-full min-h-0 overflow-y-auto overscroll-contain max-h-[300px]',
              'flex flex-col gap-xs p-sm [&>li]:shrink-0',
            ]"
            role="listbox"
            @keydown="handleMenuKeydown"
          >
            <li
              v-if="canSelectAll && !searchQueryInternal && $slots.all"
              :ref="(el) => refActiveOption(el, 'all')"
              :aria-selected="activeOption === 'all' ? 'true' : false"
              role="option"
              tabindex="0"
              class="state-effects outline-inner rounded-md cursor-pointer"
              @click="selectOption('all')"
            >
              <slot name="all" :active="activeOption === 'all'">
                <div :class="{ 'bg-primary/20': activeOption === 'all' }">
                  {{ $t('general.all') }}
                </div>
              </slot>
            </li>
            <li
              v-for="(option, index) in visibleOptions"
              :key="index"
              :ref="(el) => refActiveOption(el, option)"
              :aria-selected="activeOption === option ? 'true' : false"
              role="option"
              tabindex="0"
              class="state-effects outline-inner rounded-md cursor-pointer"
              @click="selectOption(option)"
            >
              <slot :option="option" :active="activeOption === option">
                <div :class="{ 'bg-primary/20': activeOption === option }">
                  {{ option }}
                </div>
              </slot>
            </li>
            <li v-if="hiddenResultCount > 0" role="presentation" class="px-md py-sm text-footer text-secondary">
              <slot name="moreResults" :count="hiddenResultCount">
                {{ $t('general.moreResultsRefineSearch', { count: hiddenResultCount }, hiddenResultCount) }}
              </slot>
            </li>
            <slot name="emptyPlaceholder" :query="searchQueryInternal" :results="filteredOptions">
              <EmptyPlaceholder
                v-if="showEmptyPlaceholder"
                :data="filteredOptions"
                :message="emptyMessage ?? $t('general.noResults')"
              />
            </slot>
          </ul>
        </div>
      </div>
    </PositionablePopover>
  </div>
</template>
